import React, { useEffect, useMemo, useState } from 'react';
import { Send, Loader2, FileText } from 'lucide-react';
import Swal from 'sweetalert2';

import { useMailDrafts } from './MailDraftsContext';
import { getMembershipApplication, setMembershipApplicationStatus } from '../../../../services/membershipApplications';

import {
  sendMembershipMail,
} from '../../../../services/membershipmailsService';

import {
  pickBestTo,
  renderMailDraft,
} from '../../../../services/mailTemplatesService';

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export default function SendDeclarationModal({
  open,
  app,
  onClose,
  onChanged,
}) {
  const { acceptDraft } = useMailDrafts();

  // Verificar si existe el documento de declaración jurada generado
  const hasDeclarationPdf = useMemo(() => {
    if (!app) return false;
    const declarationPdf = app?.answers?.declarationPdf || {};
    return !!(
      app.declarationPdfUrl ||
      declarationPdf.viewUrl ||
      declarationPdf.secureUrl ||
      declarationPdf.downloadUrl
    );
  }, [app]);

  // Render del draft con tokens -> valores por defecto
  const rendered = useMemo(() => renderMailDraft(acceptDraft || { subject: '', body: '' }, app), [acceptDraft, app]);
  const defaultTo = useMemo(() => pickBestTo(app), [app]);

  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(defaultTo || '');
  const [ccLine, setCcLine] = useState('');
  const [subject, setSubject] = useState(rendered.subject);
  const [body, setBody] = useState(rendered.body);
  const [copyToMe, setCopyToMe] = useState(true);
  const [attachDeclaration, setAttachDeclaration] = useState(true);

  useEffect(() => {
    if (!open) return;
    const { subject: s, body: b } = renderMailDraft(acceptDraft || { subject: '', body: '' }, app);
    setSubject(s);
    setBody(b);
    setTo(pickBestTo(app) || '');
    setCcLine('');
    setCopyToMe(true);
    setAttachDeclaration(true);
  }, [open, acceptDraft, app]);

  if (!open || !app?.id) return null;

  const send = async () => {
    if (!to || !emailRx.test(to)) {
      Swal.fire('Correo inválido', 'Completa un destinatario válido.', 'warning');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      Swal.fire('Faltan datos', 'Completa asunto y cuerpo del correo.', 'warning');
      return;
    }

    // Obtener nombre de la empresa para el mensaje de confirmación
    const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${to.trim()}</strong> para la empresa <strong>${companyName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    try {
      setSending(true);

      // Mostrar aviso de "Enviando..."
      Swal.fire({
        title: 'Enviando...',
        text: 'Por favor espera mientras se envía la declaración jurada.',
        icon: 'info',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Enviar correo con declaración jurada usando template ACCEPTED
      // El backend cambiará automáticamente el estado a WAITING_RESPONSE
      await sendMembershipMail(app.id, {
        template: 'ACCEPTED',
        to: to.trim(),
        cc: ccLine,
        subject: subject.trim(),
        body: body.trim(),
        copyToMe: !!copyToMe,
        attachDeclaration: !!attachDeclaration,
        attachApplicationPdf: false,
      });

      // Recargar la aplicación para obtener el estado actualizado
      const updatedApp = await getMembershipApplication(app.id);
      const updated = updatedApp?.data || updatedApp || app;

      // Obtener nombre de la empresa
      const companyName = updated?.razonSocial || updated?.nombreFantasia || app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

      Swal.fire({
        title: 'Enviado',
        html: `Correo con declaración jurada "${subject.trim()}" enviado correctamente a ${to.trim()} para la empresa <strong>${companyName}</strong>. El estado cambió a "Esperando respuesta". Se esperará la declaración firmada del socio.`,
        icon: 'success'
      });
      onChanged?.(updated || app);
      onClose?.();
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo completar el envío.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-center justify-center p-2 sm:p-3">
      <div className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF] text-white flex items-center justify-between">
          <div className="font-semibold">Enviar declaración jurada</div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
            <span className="text-base leading-none">ℹ️</span>
            <span>
              Se adjuntará la <span className="font-bold text-blue-900">Declaración jurada de ingreso (.docx)</span> por defecto. El documento se generará automáticamente si aún no existe. El estado cambiará a <span className="font-bold text-blue-900">"Esperando respuesta"</span> después del envío.
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">Para</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="correo@empresa.cl"
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">CC (opcional)</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={ccLine}
                onChange={(e) => setCcLine(e.target.value)}
                placeholder="cc1@dom.com, cc2@dom.com"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Asunto</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Asunto del correo"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Cuerpo</div>
              <textarea
                className="w-full rounded-lg border px-3 py-2 text-sm"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
          </div>

          {/* Opciones */}
          <div className="rounded-xl border p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-2">Adjuntos & opciones</div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={copyToMe}
                onChange={(e) => setCopyToMe(e.target.checked)}
              />
              <span className="text-sm text-gray-700">Con copia al correo por defecto</span>
            </label>

            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={attachDeclaration}
                onChange={(e) => setAttachDeclaration(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Adjuntar declaración jurada de ingreso (.docx)
                {hasDeclarationPdf && (
                  <span className="ml-1 text-xs text-green-600">(ya generado)</span>
                )}
              </span>
            </label>

            {attachDeclaration && (
              <div className="mt-2 text-xs inline-flex items-center gap-2 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-700">
                <FileText size={14} /> 
                {hasDeclarationPdf 
                  ? 'Se adjuntará la Declaración Jurada (.docx ya generado).'
                  : 'Se generará y adjuntará la Declaración Jurada (.docx) automáticamente.'
                }
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex justify-end gap-2 border-t bg-gray-50 flex-none">
          <button
            onClick={async () => {
              // Obtener nombre de la empresa
              const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';
              
              const result = await Swal.fire({
                title: '¿Cancelar envío?',
                html: `Si cancelas, <strong>no se enviará el correo</strong> y <strong>no se efectuará el cambio de estado</strong>. La postulación de la empresa <strong>${companyName}</strong> permanecerá en su estado actual.`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#6b7280',
                cancelButtonColor: '#0488AF',
                confirmButtonText: 'Sí, cancelar',
                cancelButtonText: 'Continuar enviando',
              });
              
              if (result.isConfirmed) {
                onClose?.();
              }
            }}
            className="h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2 text-sm"
          >
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Enviar declaración jurada
          </button>
        </div>
      </div>
    </div>
  );
}

